import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Card,
    Checkbox,
    Col,
    Descriptions,
    Empty,
    List,
    Row,
    Space,
    Spin,
    Tag,
    Timeline,
    Typography,
    message
} from 'antd';

const { Title, Text, Paragraph } = Typography;

const STATUS_COLORS = {
    1: 'blue',
    2: 'default',
    3: 'processing',
    4: 'gold',
    5: 'success',
    6: 'orange',
    7: 'error'
};

/**
 * Содержимое карточки задачи Bitrix (для Drawer / страницы).
 */
const BitrixTaskDetailPanel = ({ taskId, compactHeader = false }) => {
    const [loading, setLoading] = useState(true);
    const [task, setTask] = useState(null);

    useEffect(() => {
        if (!taskId) {
            setTask(null);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setTask(null);
            try {
                const token = localStorage.getItem('token');
                const { data } = await axios.get(
                    `/api/bitrix-tasks/${taskId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (!cancelled) setTask(data.task);
            } catch (error) {
                if (!cancelled) {
                    const status = error.response?.status;
                    message.error(
                        error.response?.data?.error ||
                            (status === 403
                                ? 'Нет доступа к задаче'
                                : 'Задача не найдена')
                    );
                    setTask(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [taskId]);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 64 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!task) {
        return <Empty description="Задача не найдена" />;
    }

    const comments = task.comments || [];
    const checklist = task.checklist || [];
    const history = task.history || [];
    const files = task.files || [];

    return (
        <Space
            direction="vertical"
            size="large"
            style={{ width: '100%' }}
        >
            {!compactHeader ? (
                <div>
                    <Title level={3} style={{ marginTop: 0 }}>
                        #{task.id} {task.title}
                    </Title>
                    <Space wrap>
                        <Tag
                            color={
                                STATUS_COLORS[task.status] ||
                                'default'
                            }
                        >
                            {task.status_name || task.status}
                        </Tag>
                        {task.is_overdue ? (
                            <Tag color="error">Просрочена</Tag>
                        ) : null}
                    </Space>
                </div>
            ) : (
                <Space wrap>
                    <Tag
                        color={
                            STATUS_COLORS[task.status] || 'default'
                        }
                    >
                        {task.status_name || task.status}
                    </Tag>
                    {task.is_overdue ? (
                        <Tag color="error">Просрочена</Tag>
                    ) : null}
                </Space>
            )}

            <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Исполнитель">
                    {task.participants?.responsible?.name ||
                        task.responsible_name ||
                        task.responsible_id ||
                        '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Постановщик">
                    {task.participants?.creator?.name ||
                        task.created_by_name ||
                        task.created_by ||
                        '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Соисполнители" span={2}>
                    {(task.participants?.accomplices ||
                        task.accomplices ||
                        []
                    ).length
                        ? (
                              task.participants?.accomplices ||
                              task.accomplices
                          )
                              .map(p =>
                                  typeof p === 'object'
                                      ? p.name || `ID ${p.id}`
                                      : `ID ${p}`
                              )
                              .join(', ')
                        : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Наблюдатели" span={2}>
                    {(task.participants?.auditors ||
                        task.auditors ||
                        []
                    ).length
                        ? (task.participants?.auditors || task.auditors)
                              .map(p =>
                                  typeof p === 'object'
                                      ? p.name || `ID ${p.id}`
                                      : `ID ${p}`
                              )
                              .join(', ')
                        : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Группа / проект">
                    {task.group_name || task.group_id || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Дедлайн">
                    {task.deadline
                        ? new Date(task.deadline).toLocaleString(
                              'ru-RU'
                          )
                        : '—'}
                </Descriptions.Item>
            </Descriptions>

            {task.description ? (
                <Card title="Описание" size="small">
                    <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                        {task.description}
                    </Paragraph>
                </Card>
            ) : null}

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card
                        title={`Чат / комментарии (${comments.length})`}
                        size="small"
                    >
                        {comments.length ? (
                            <List
                                dataSource={comments}
                                renderItem={c => (
                                    <List.Item>
                                        <List.Item.Meta
                                            title={
                                                <Space>
                                                    <Text strong>
                                                        {c.author_name ||
                                                            c.author_id ||
                                                            '—'}
                                                    </Text>
                                                    <Text type="secondary">
                                                        {c.bitrix_created_at
                                                            ? new Date(
                                                                  c.bitrix_created_at
                                                              ).toLocaleString(
                                                                  'ru-RU'
                                                              )
                                                            : ''}
                                                    </Text>
                                                </Space>
                                            }
                                            description={
                                                <div
                                                    style={{
                                                        whiteSpace:
                                                            'pre-wrap'
                                                    }}
                                                >
                                                    {c.post_message ||
                                                        '—'}
                                                </div>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        ) : (
                            <Empty description="Нет комментариев" />
                        )}
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card
                        title={`Чеклист (${checklist.length})`}
                        size="small"
                        style={{ marginBottom: 16 }}
                    >
                        {checklist.length ? (
                            <Space
                                direction="vertical"
                                style={{ width: '100%' }}
                            >
                                {checklist.map(item => (
                                    <Checkbox
                                        key={item.id}
                                        checked={Boolean(
                                            item.is_complete
                                        )}
                                        disabled
                                    >
                                        {item.title}
                                    </Checkbox>
                                ))}
                            </Space>
                        ) : (
                            <Empty description="Пусто" />
                        )}
                    </Card>

                    <Card
                        title={`Файлы (${files.length})`}
                        size="small"
                        style={{ marginBottom: 16 }}
                    >
                        {files.length ? (
                            <List
                                size="small"
                                dataSource={files}
                                renderItem={f => (
                                    <List.Item>
                                        {f.local_path ? (
                                            <a
                                                href={`/api/bitrix-tasks/${task.id}/files/${f.id}/download`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {f.name ||
                                                    `Файл ${f.id}`}
                                            </a>
                                        ) : (
                                            <Text>
                                                {f.name ||
                                                    `Файл ${f.id}`}
                                            </Text>
                                        )}
                                    </List.Item>
                                )}
                            />
                        ) : (
                            <Empty description="Нет файлов" />
                        )}
                    </Card>

                    <Card
                        title={`История (${history.length})`}
                        size="small"
                    >
                        {history.length ? (
                            <Timeline
                                items={history.map(h => ({
                                    children: (
                                        <div>
                                            <Text strong>
                                                {h.field ||
                                                    'изменение'}
                                            </Text>
                                            <br />
                                            <Text type="secondary">
                                                {h.user_name ||
                                                    h.user_id ||
                                                    ''}{' '}
                                                {h.bitrix_created_at
                                                    ? new Date(
                                                          h.bitrix_created_at
                                                      ).toLocaleString(
                                                          'ru-RU'
                                                      )
                                                    : ''}
                                            </Text>
                                            {(h.from_value ||
                                                h.to_value) && (
                                                <div>
                                                    <Text
                                                        delete
                                                        type="secondary"
                                                    >
                                                        {h.from_value ||
                                                            '—'}
                                                    </Text>
                                                    {' → '}
                                                    <Text>
                                                        {h.to_value ||
                                                            '—'}
                                                    </Text>
                                                </div>
                                            )}
                                        </div>
                                    )
                                }))}
                            />
                        ) : (
                            <Empty description="Нет истории" />
                        )}
                    </Card>
                </Col>
            </Row>
        </Space>
    );
};

export default BitrixTaskDetailPanel;
export { STATUS_COLORS };
